import MarketingLayout from '../components/marketing/MarketingLayout';
import { colors } from '../lib/colors';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import ResponsiveHeroImage from '../components/common/ResponsiveHeroImage';

export default function About() {
  return (
    <MarketingLayout>
      <section className="min-h-screen relative overflow-hidden bg-slate-900">
        {/* Mountain background image with mobile optimization */}
        <div className="absolute inset-0">
          <div className="relative w-full h-full opacity-50 md:opacity-50">
            <ResponsiveHeroImage 
              src="/assets/mountains-1.png" 
              alt="Mountain landscape background" 
              className="absolute inset-0 w-full h-full"
              loading="eager"
              priority={true}
              mobileFocalPoint="custom"
              desktopPosition="center 30%"
              mobilePosition="center 40%"  // Lowered to show more of the mountain
            />
          </div>
          {/* Enhanced gradient overlay for mobile readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/60 via-slate-900/70 to-slate-900/90 md:from-slate-900/40 md:via-slate-900/60 md:to-slate-900/80" />
        </div>
        
        {/* Subtle texture overlay */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,0.05) 35px, rgba(255,255,255,0.05) 70px)`,
          }} />
        </div>
        
        {/* Gold accent elements - more subtle now */}
        <div className="absolute top-0 right-0 w-96 h-96 opacity-10">
          <div className="w-full h-full rounded-full bg-gradient-to-br from-amber-600 to-amber-800 blur-3xl transform translate-x-1/2 -translate-y-1/2" />
        </div>
        <div className="absolute bottom-0 left-0 w-64 h-64 opacity-10">
          <div className="w-full h-full rounded-full bg-gradient-to-tr from-amber-700 to-amber-900 blur-2xl transform -translate-x-1/2 translate-y-1/2" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto py-24 px-6">
          {/* Hero section with gold accent */}
          <div className="mb-20">
            <div className="inline-block mb-6">
              <div className="h-1 w-20 bg-gradient-to-r from-amber-600 to-amber-500 rounded-full" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-8 text-white">
              About Our Firm
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl leading-relaxed">
              Like the enduring strength of natural elements, we build lasting financial 
              legacies that withstand the test of time.
            </p>
          </div>

          {/* Content sections with visual hierarchy */}
          <div className="grid md:grid-cols-2 gap-12 mb-20">
            <div className="space-y-8">
              <div className="relative">
                <div className="absolute -left-4 top-0 w-1 h-full bg-gradient-to-b from-amber-600 to-transparent rounded-full" />
                <div className="pl-8">
                  <h2 className="text-2xl font-semibold mb-4 text-white">Our Expertise</h2>
                  <p className="text-gray-300 leading-relaxed">
                    We specialize in advanced insurance and financial strategies for 
                    high-net-worth Canadians. Our team combines decades of expertise 
                    and elite designations (CFP, TEP) to deliver comprehensive, tax-efficient 
                    solutions that preserve wealth, secure business legacies, and protect 
                    multi-generational families.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-8">
              <div className="relative">
                <div className="absolute -left-4 top-0 w-1 h-full bg-gradient-to-b from-amber-600 to-transparent rounded-full" />
                <div className="pl-8">
                  <h2 className="text-2xl font-semibold mb-4 text-white">Our Mission</h2>
                  <p className="text-gray-300 leading-relaxed">
                    Our mission is straightforward: to help you retain more of your hard-earned 
                    wealth while ensuring future generations thrive. Through personalized service 
                    and cutting-edge solutions, we aim to set the standard for HNW insurance 
                    advisory across Canada.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Values section with cards */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold mb-12 text-center text-white">Our Core Values</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-slate-800/50 backdrop-blur-sm p-8 rounded-lg border border-slate-700/50 hover:border-amber-600/30 transition-all duration-300">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-600 to-amber-700 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-white font-bold text-xl">1</span>
                </div>
                <h3 className="text-xl font-semibold mb-3 text-white">Excellence</h3>
                <p className="text-gray-400">
                  Pursuing the highest standards in financial advisory and wealth preservation strategies.
                </p>
              </div>
              
              <div className="bg-slate-800/50 backdrop-blur-sm p-8 rounded-lg border border-slate-700/50 hover:border-amber-600/30 transition-all duration-300">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-600 to-amber-700 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-white font-bold text-xl">2</span>
                </div>
                <h3 className="text-xl font-semibold mb-3 text-white">Integrity</h3>
                <p className="text-gray-400">
                  Building trust through transparency, ethical practices, and unwavering commitment to your interests.
                </p>
              </div>
              
              <div className="bg-slate-800/50 backdrop-blur-sm p-8 rounded-lg border border-slate-700/50 hover:border-amber-600/30 transition-all duration-300">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-600 to-amber-700 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-white font-bold text-xl">3</span>
                </div>
                <h3 className="text-xl font-semibold mb-3 text-white">Legacy</h3>
                <p className="text-gray-400">
                  Creating enduring financial structures that protect and grow wealth across generations.
                </p>
              </div>
            </div>
          </div>

          {/* CTA section */}
          <div className="text-center">
            <div className="inline-block">
              <Link to="/contact">
                <button className="group bg-gradient-to-r from-amber-600 to-amber-700 text-white px-8 py-4 rounded-lg font-semibold hover:from-amber-700 hover:to-amber-800 transition-all duration-300 flex items-center gap-3">
                  Begin Your Journey
                  <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </Link>
            </div>
            <p className="mt-4 text-gray-400 text-sm">
              Discover how we can help preserve your wealth for generations
            </p>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}